import React, {
  useState,
  useCallback,
  createContext,
  PropsWithChildren,
} from 'react';
import {createPortal} from 'react-dom';
import styled from 'styled-components';
import {Toast, ToastTypes} from './Toast';
import {useToastPortalElement} from '../../../hooks/usePortalElement/useToastPortalElement';
import {Z_INDEXES} from '../../../constants/layers';

export type ShowToastProps = PropsWithChildren<{
  // callback triggered when toast disappears after the specified amount of ms
  onAutoClose?(): void;
  // callback triggered when clicking the close button
  onDismiss?(): void;
  // hide close button within the toast
  hideCloseButton?: boolean;
  // flag that sets the toast to be full-size, from border to border when set to true
  detachedFromBorders?: boolean;
  // prop that determines the styling of the toast based on the defined types
  type: ToastTypes;
  // sets the toast display animation direction. Note: It refers only the "incoming direction" as the placement of the toast will be the opposite.
  direction?: 'up' | 'down' | 'left' | 'right';
  // offset to the positioning of the toast
  offset?: string;
  // content rendered inside the toast
  content: React.ReactElement | string;
  // used for testing purposes
  ['data-testid']?: string;
}>;

interface ToastProps extends ShowToastProps {
  uuid: number;
}

interface ProviderProps {
  maxToasts?: number;
}

// @ts-ignore
export const ToastContext = createContext((toastProps: ShowToastProps) => {});

export const ToastContextProvider: React.FC<
  React.PropsWithChildren<ProviderProps>
> = ({
  // should add prop for max number of toasts displayed
  children,
  maxToasts,
}) => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const toastPortalElement = useToastPortalElement();

  const removeToast = (toastUuid: number) => {
    setToasts(toasts.filter(toast => toast.uuid !== toastUuid));
  };

  const showToast = useCallback(
    (toast: ShowToastProps) => {
      const updatedToasts =
        // if the length of the currently stored toasts plus the newly added one is greater than the maxToasts we will remove the first toast from the array
        maxToasts && toasts.length + 1 > maxToasts ? toasts.slice(1) : toasts;
      // we need to add a uuid to each toast in order to be able to remove them properly
      const appendedToasts = [
        ...updatedToasts,
        {...toast, uuid: performance.now()},
      ];
      setToasts(appendedToasts);
    },
    [toasts, maxToasts],
  );

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      {toastPortalElement &&
        createPortal(
          <ToastsContainer>
            {toasts.map(toast => (
              <Toast
                {...toast}
                autoHide={true}
                autoHideTimeoutMs={5000}
                key={toast.uuid}
                isShown={true}
                onDismiss={() => {
                  toast.onDismiss?.();
                  removeToast(toast.uuid);
                }}
                onAutoClose={() => {
                  toast.onAutoClose?.();
                  removeToast(toast.uuid);
                }}
              >
                {toast.content}
              </Toast>
            ))}
          </ToastsContainer>,
          toastPortalElement,
        )}
    </ToastContext.Provider>
  );
};

const ToastsContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: ${Z_INDEXES.TADARIDA_TOAST_CONTAINER};
  display: flex;
  flex-direction: column;
`;
